'use client';

type Props = {
  isRecording: boolean;
  isTranscribing: boolean;
  error: string | null;
  isSupported: boolean;
  onStart: () => void;
  onStop: () => void;
};

export function Recorder({ isRecording, isTranscribing, error, isSupported, onStart, onStop }: Props) {
  if (!isSupported) {
    return (
      <div style={{
        background: 'rgba(239, 68, 68, 0.1)',
        border: '1px solid rgba(239, 68, 68, 0.3)',
        borderRadius: '16px',
        padding: '24px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
        <p style={{ color: '#fca5a5', fontWeight: 'bold', fontSize: '16px' }}>マイクにアクセスできません</p>
        <p style={{ color: 'rgba(255,255,255,0.6)', marginTop: '8px', fontSize: '14px' }}>ブラウザの設定でマイクを許可してください</p>
      </div>
    );
  }

  const buttonStyle: React.CSSProperties = {
    position: 'relative',
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '14px',
    border: 'none',
    cursor: isTranscribing ? 'not-allowed' : 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
    transition: 'all 0.3s ease',
    background: isRecording
      ? 'linear-gradient(135deg, #ef4444, #ec4899)'
      : isTranscribing
      ? 'linear-gradient(135deg, #9333ea, #6366f1)'
      : 'linear-gradient(135deg, #10b981, #06b6d4)',
    opacity: isTranscribing ? 0.7 : 1,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
      <div style={{ position: 'relative' }}>
        <button
          onClick={isRecording ? onStop : onStart}
          disabled={isTranscribing}
          style={buttonStyle}
        >
          {isTranscribing ? (
            <>
              <div style={{
                width: '28px',
                height: '28px',
                border: '3px solid rgba(255,255,255,0.3)',
                borderTopColor: 'white',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              <span style={{ fontSize: '12px' }}>認識中...</span>
            </>
          ) : isRecording ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', height: '32px' }}>
                <div style={{ width: '4px', backgroundColor: 'white', borderRadius: '9999px', animation: 'wave 1s ease-in-out 0s infinite' }} />
                <div style={{ width: '4px', backgroundColor: 'white', borderRadius: '9999px', animation: 'wave 1s ease-in-out 0.1s infinite' }} />
                <div style={{ width: '4px', backgroundColor: 'white', borderRadius: '9999px', animation: 'wave 1s ease-in-out 0.2s infinite' }} />
                <div style={{ width: '4px', backgroundColor: 'white', borderRadius: '9999px', animation: 'wave 1s ease-in-out 0.3s infinite' }} />
                <div style={{ width: '4px', backgroundColor: 'white', borderRadius: '9999px', animation: 'wave 1s ease-in-out 0.4s infinite' }} />
              </div>
              <span style={{ fontSize: '12px' }}>タップで停止</span>
            </>
          ) : (
            <>
              <span style={{ fontSize: '36px' }}>🎤</span>
              <span style={{ fontSize: '12px' }}>タップで録音</span>
            </>
          )}
        </button>
      </div>

      {isRecording && (
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 16px',
          borderRadius: '9999px',
          background: 'rgba(239, 68, 68, 0.2)',
          border: '1px solid rgba(239, 68, 68, 0.3)'
        }}>
          <span style={{
            width: '8px',
            height: '8px',
            backgroundColor: '#ef4444',
            borderRadius: '50%',
            animation: 'pulse 1s ease-in-out infinite'
          }} />
          <span style={{ color: '#fca5a5', fontWeight: '500', fontSize: '14px' }}>録音中...</span>
        </div>
      )}

      {isTranscribing && (
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 16px',
          borderRadius: '9999px',
          background: 'rgba(147, 51, 234, 0.2)',
          border: '1px solid rgba(147, 51, 234, 0.3)'
        }}>
          <span style={{ color: '#c4b5fd', fontWeight: '500', fontSize: '14px' }}>🧠 AIが音声を認識しています...</span>
        </div>
      )}

      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '12px',
          padding: '12px 16px'
        }}>
          <p style={{ color: '#fca5a5', textAlign: 'center', fontSize: '14px' }}>{error}</p>
        </div>
      )}

      {!isRecording && !isTranscribing && !error && (
        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', textAlign: 'center' }}>
          録音ボタンをタップして、今日の記録を話してください
        </p>
      )}

      <style>{`
        @keyframes wave {
          0%, 100% { height: 8px; }
          50% { height: 24px; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
